import * as React from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  InputAdornment,
  IconButton,
  ListSubheader,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import StarsIcon from '@mui/icons-material/Stars';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../service';

const MenuProps = {
  // Sem isto, cada letra digitada na busca refiltra a lista, o 1º item novo monta
  // com autoFocus e rouba o foco da caixa: só a 1ª letra entrava.
  disableAutoFocusItem: true,
  PaperProps: {
    sx: {
      maxHeight: 420,
      '& .search-sticky': {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      },
    },
  },
  MenuListProps: {
    sx: {
      pt: 0,   
      pb: 0, 
    },
  }
};

// label e renderValue são opcionais: o filtro do painel usa o padrão, a aba de
// relatórios chama de "Lojas" e escreve "Todas as N lojas" no campo fechado.
export function MultipleSelectUnidade({ unidadeList = [], unidade = [], handleChangeTo, label = 'Unidades', renderValue }) {
  const safeInitial = Array.isArray(unidade) ? unidade : [];
  const [unidadeSelected, setUnidadeSelected] = React.useState(safeInitial);
  const [search, setSearch] = React.useState('');

  // Garante que sempre trabalharemos com um array
  const safeUnidadeList = Array.isArray(unidadeList) ? unidadeList : [];

  // Sincroniza quando o valor externo muda (ex: modal reabre com seleção prévia)
  React.useEffect(() => {
    const incoming = Array.isArray(unidade) ? unidade : [];
    setUnidadeSelected(incoming);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(unidade)]);

  // Auto-seleciona quando há apenas uma unidade disponível
  React.useEffect(() => {
    if (safeUnidadeList.length === 1 && unidadeSelected.length === 0) {
      const singleValue = JSON.stringify({
        idUnidade: safeUnidadeList[0].idUnidade,
        nome: safeUnidadeList[0].nome,
      });
      setUnidadeSelected([singleValue]);
      handleChangeTo([singleValue]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeUnidadeList.length]);

  const isAllSelected =
    safeUnidadeList.length > 0 &&
    unidadeSelected.length === safeUnidadeList.length;

  const normalized = (s) =>
    (s ?? '')
      .toString()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();

  const filteredList = React.useMemo(() => {
    const q = normalized(search);
    if (!q) return safeUnidadeList;

    return safeUnidadeList.filter((u) => {
      const idOk = normalized(String(u.idUnidade)).includes(q);
      const nomeOk = normalized(u.nome).includes(q);
      return idOk || nomeOk;
    });
  }, [search, safeUnidadeList]);

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;

    const hasAll = value?.some((item) => item === 'all');

    // Clique em "Selecionar todas" quando já está tudo selecionado => limpa
    if (hasAll && isAllSelected) {
      setUnidadeSelected([]);
      handleChangeTo([]);
      return;
    }

    // Clique em "Selecionar todas" quando ainda não está tudo selecionado => marca todas
    if (hasAll) {
      const allUnidades = safeUnidadeList.map((unidade) =>
        JSON.stringify({ idUnidade: unidade.idUnidade, nome: unidade.nome })
      );
      setUnidadeSelected(allUnidades);
      handleChangeTo(allUnidades);
      return;
    }

    // Seleção normal
    setUnidadeSelected(value);
    handleChangeTo(value);
  };

  const clearSearch = (e) => {
    e.stopPropagation();
    setSearch('');
  };

  return (
    <div>
      <FormControl fullWidth>
        <InputLabel id="multiple-checkbox-label" shrink={renderValue ? true : undefined}>{label}</InputLabel>
        <Select
          labelId="multiple-checkbox-label"
          id="multiple-checkbox"
          multiple
          value={unidadeSelected}
          onChange={handleChange}
          // Com renderValue proprio o campo vazio tambem escreve algo ("Nenhuma loja escolhida").
          displayEmpty={!!renderValue}
          input={<OutlinedInput fullWidth label={label} notched={renderValue ? true : undefined} />}
          renderValue={(selected) => (renderValue
            ? renderValue(selected, safeUnidadeList)
            : `${selected?.length || 0} unidade(s) selecionada(s)`
          )}
          MenuProps={MenuProps}
        >
          {/* Cabeçalho com busca (fixo no topo, não fecha o menu) */}
          <ListSubheader
            sx={{ pt: 1.5}}
          >
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Buscar por ID ou nome…"
              value={search}
              onChange={(e) => setSearch(e.target.value || '')}
              onKeyDown={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label="Limpar"
                      onClick={clearSearch}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </ListSubheader>

          {/* Selecionar todas */}
          <MenuItem key="all" value="all">
            <Checkbox checked={isAllSelected} />
            <ListItemText primary="Selecionar todas" />
          </MenuItem>

          {/* Lista de unidades filtradas */}
          {filteredList.map((unidade) => (
            <MenuItem
              key={unidade.idUnidade}
              value={JSON.stringify({
                idUnidade: unidade.idUnidade,
                nome: unidade.nome,
              })}
            >
              <Checkbox checked={checked(unidadeSelected, unidade)} />
              <ListItemText primary={renderUnidade(unidade)} />
            </MenuItem>
          ))}

          {filteredList.length === 0 && (
            <MenuItem disabled>
              <Box px={1} color="text.secondary">
                Nenhuma unidade encontrada
              </Box>
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </div>
  );
}

function renderUnidade(unidade) {
  if (!unidade) return '';
  return `${unidade.idUnidade ?? ''} - ${unidade.nome ?? ''}`.trim();
}

function checked(unidadeSelected, unidade) {
  if (!unidade) return false;

  const key = JSON.stringify({
    idUnidade: unidade.idUnidade,
    nome: unidade.nome,
  });

  return unidadeSelected.includes(key);
}

export function MultipleSelectUnidadeBasic({ label = 'Unidades' } = {}) {
  const navigate = useNavigate();

  // Usuário sem plano PRO sempre opera sobre a própria unidade do perfil.
  // O nome aparecia em branco porque o componente recebia um array de strings
  // (unidadeSelected) e tentava ler `.nome` dele como se fosse um objeto único.
  const profile = new AuthService().getProfile();
  const unidade = profile?.unidade || profile?.unidadeList?.[0];
  const unidadeId = unidade?.idUnidade != null ? String(unidade.idUnidade) : '';

  function handlePro() {
    navigate('/unauthorized/ROLE_SISCHEF_ANALYTICS_PRO');
  }

  return (
    <FormControl fullWidth>
      <InputLabel id="multiple-checkbox-label">{label}</InputLabel>
      <Select
        multiple
        value={unidadeId ? [unidadeId] : []}
        input={<OutlinedInput fullWidth label={label} />}
        renderValue={() => unidade?.nome ?? ''}
        MenuProps={MenuProps}
      >
        <MenuItem value={unidadeId}>
          <Checkbox disabled checked />
          <ListItemText primary={renderUnidade(unidade)} />
        </MenuItem>

        <MenuItem onClick={handlePro}>
          <Box px={1}>
            <StarsIcon sx={{ color: 'gold' }} />
          </Box>
          <ListItemText primary="Deseja comparar com outras empresas? Conheça o plano PRO!" />
        </MenuItem>
      </Select>
    </FormControl>
  );
}
